import type { Project } from '@/api/client';
import { PROJECT_STAGES } from '@stages/stages';
import { getBaseStatus, getSubstatus, SUBSTATUSES } from './projectStatuses';

/** Порядок статусов: Готов вверху (0), Добавлен внизу. Меньший индекс = выше в таблице. Цепочка — общий модуль этапов */
const STATUS_ORDER: Record<string, number> = Object.fromEntries(
  [...PROJECT_STAGES].reverse().map((stage, index) => [stage, index]),
);

function parseDate(value: string | null | undefined): number {
  if (!value) return Infinity;
  const t = new Date(value).getTime();
  return isNaN(t) ? Infinity : t;
}

/**
 * Сортировка проектов для таблицы:
 * - По базовому статусу: внизу Добавлен, вверху Готов (подстатусы не влияют)
 * - Внутри статуса (кроме Готов): с дедлайном выше, без дедлайна — внизу; среди с дедлайном — по дате (ближе выше);
 *   среди без дедлайна — по дате добавления (старше выше, недавние ниже)
 * - Готов: по дате готовности (completed_at), самые свежезаконченные внизу
 */
export function sortProjects(projects: Project[]): Project[] {
  return [...projects].sort((a, b) => {
    const baseA = getBaseStatus(a.state);
    const baseB = getBaseStatus(b.state);
    const orderA = STATUS_ORDER[baseA] ?? 0;
    const orderB = STATUS_ORDER[baseB] ?? 0;
    if (orderA !== orderB) return orderA - orderB;

    if (baseA === 'Готов') {
      const completedA = parseDate(a.completed_at ?? a.updated_at ?? a.created_at);
      const completedB = parseDate(b.completed_at ?? b.updated_at ?? b.created_at);
      return completedA - completedB;
    }

    const deadlineA = parseDate(a.deadline);
    const deadlineB = parseDate(b.deadline);
    const createdA = parseDate(a.created_at ?? a.updated_at);
    const createdB = parseDate(b.created_at ?? b.updated_at);

    const hasDeadlineA = deadlineA !== Infinity;
    const hasDeadlineB = deadlineB !== Infinity;

    if (hasDeadlineA && !hasDeadlineB) return -1;
    if (!hasDeadlineA && hasDeadlineB) return 1;
    if (hasDeadlineA && hasDeadlineB) return deadlineA - deadlineB;
    return createdA - createdB;
  });
}

/**
 * Порядок для подменю/комбо выбора проекта (обратный таблице):
 * Добавлен → Запись → Монтаж → Проверка → Правки (подстатусы по цепочке до «Внесены») → Проверка правок → Готов.
 * Внутри одного полного статуса: с дедлайном выше (срочнее = раньше дата), без дедлайна ниже, по имени.
 */
const PICKER_BASE_ORDER: Record<string, number> = Object.fromEntries(
  PROJECT_STAGES.map((stage, index) => [stage, index]),
);

function pickerBaseRank(state: string | null | undefined): number {
  const base = getBaseStatus(state);
  return PICKER_BASE_ORDER[base] ?? 99;
}

/** Индекс подстатуса; без подстатуса — 0; неизвестный подстатус — в конце группы */
function pickerSubstatusRank(state: string | null | undefined): number {
  const base = getBaseStatus(state);
  const subs = SUBSTATUSES[base];
  if (!subs?.length) return 0;
  const sub = getSubstatus(state);
  if (!sub) return 0;
  const idx = (subs as readonly string[]).indexOf(sub);
  return idx === -1 ? 999 : idx;
}

function compareNameRu(a: Project, b: Project): number {
  return a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' });
}

export function sortProjectsForPickers(projects: Project[]): Project[] {
  return [...projects].sort((a, b) => {
    const brA = pickerBaseRank(a.state);
    const brB = pickerBaseRank(b.state);
    if (brA !== brB) return brA - brB;

    const srA = pickerSubstatusRank(a.state);
    const srB = pickerSubstatusRank(b.state);
    if (srA !== srB) return srA - srB;

    const deadlineA = parseDate(a.deadline);
    const deadlineB = parseDate(b.deadline);
    const hasDeadlineA = deadlineA !== Infinity;
    const hasDeadlineB = deadlineB !== Infinity;

    if (hasDeadlineA && !hasDeadlineB) return -1;
    if (!hasDeadlineA && hasDeadlineB) return 1;
    if (hasDeadlineA && hasDeadlineB && deadlineA !== deadlineB) return deadlineA - deadlineB;

    return compareNameRu(a, b);
  });
}
